import { Box, Slider, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { THRESHOLD, TIMEFRAME } from 'src/global/config/common.config';
import type { ProjectAdminWalletType } from 'src/global/schemas/project.schema';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { formatNumberWithComma } from 'src/utils/helpers';
import { isAllowedGreadterThanZero } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';
import { StepAccordion } from '../StepAccordion';
import { DistributionPhase } from './DistributionPhase';
import { FundingRecipients } from './FundingRecipients';

export interface FundingDistributionProps {
  adminWallet?: ProjectAdminWalletType;
  isDetail?: boolean;
}

export function FundingDistribution({ adminWallet, isDetail }: FundingDistributionProps) {
  const { hasDeployed } = useDeployment();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<ProjectInputs>();

  return (
    <StepAccordion
      isError={!!errors.recipients || !!errors.phases || !!errors.threshold}
      title='Funding Distribution Model'
      index={6}>
      <Stack rowGap={3}>
        <Typography variant='body1' fontSize={'14px'} fontWeight={400} sx={{ color: 'white' }}>
          Learn how to structure your funding distribution, set sales thresholds, and define release conditions. Visit
          the{' '}
          <a
            href={'https://link.lfg.inc/kb-coresale-fundingdistro'}
            target={'_blank'}
            style={{ color: 'white', textDecoration: 'underline' }}>
            Funding Distribution Model
          </a>{' '}
          article for more information.
        </Typography>
        <Typography fontSize={'14px'} color='white' lineHeight={'20px'} variant='h6'>
          For each node sale phase, define how funds are distributed among the project and other stakeholders. LFG
          receives a 5% platform fee.
        </Typography>
        <FundingRecipients />
        <div>
          <Typography variant='h1' fontWeight={500} fontSize={'16px'} color='white' pb={2}>
            Full Release Threshold
          </Typography>
          <Typography className='text-body_m font-gotham text-white'>
            Set a sales threshold and timeframe for your project. Once the threshold is met, funds will be released.
          </Typography>
        </div>
        <Box width={'100%'}>
          <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
            Sales Threshold
          </Typography>
          <Controller
            name='threshold.threshold'
            control={control}
            render={({ field }) => (
              <AdminNumberFormat
                readOnly={isDetail || hasDeployed}
                isAllowed={isAllowedGreadterThanZero}
                onValueChange={values => {
                  field.onChange(Number(values.floatValue));
                }}
                isError={!!errors?.threshold?.threshold}
                errorMsg={errors?.threshold?.threshold?.message}
                placeholder={`$${formatNumberWithComma(Number(THRESHOLD))}`}
                prefix='$'
                suffix={''}
                {...field}
              />
            )}
          />
          <Typography className='text-body_note text-[#A8A7AF] font-gotham mt-2'>
            Minimum: ${formatNumberWithComma(Number(THRESHOLD))}
          </Typography>
        </Box>
        <Box>
          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography className='text-head_xs font-gotham text-white'>Timeframe</Typography>
            <Typography component={'p'} fontWeight={500} fontSize={'16px'} className='font-gotham text-white'>
              {watch('threshold.timeframe')}
              <Typography component='span' className='pl-1 text-body_m font-gotham text-neutral-300'>
                days
              </Typography>
            </Typography>
          </Stack>
          <Box mx={2}>
            <Slider
              onChange={e => {
                const target = e.target as HTMLInputElement;
                setValue('threshold.timeframe', Number(target.value));
              }}
              disabled={isDetail || hasDeployed}
              min={Number(TIMEFRAME)}
              max={180}
              valueLabelDisplay='off'
              value={watch('threshold.timeframe')}
              sx={{
                '& .MuiSlider-rail': {
                  backgroundColor: colors.neutral06,
                },
                '& .MuiSlider-thumb': {
                  backgroundColor: colors.blue200,
                  boxShadow: 'none !important',
                },
                '& .MuiSlider-track': {
                  backgroundColor: colors.blue200,
                  borderColor: colors.blue200,
                },
              }}
            />
          </Box>
          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography className='text-body_m font-gotham text-white'>{Number(TIMEFRAME)}</Typography>
            <Typography className='text-body_m font-gotham text-white'>180</Typography>
          </Stack>
        </Box>
        <DistributionPhase isDisabled={isDetail} adminWallet={adminWallet} />
      </Stack>
    </StepAccordion>
  );
}
